import * as yup from 'yup';
import { TFunction } from 'i18next';
import { isPossiblePhoneNumber, CountryCode } from 'libphonenumber-js';

import {
  COUNTRIES,
  Country,
  getRequiredAddressValidationSchema,
} from '@electricjs/arc';

import { emailRegex } from '@/constants/email';
import { DeviceRetrieveAction } from '../types';

const NON_US_COUNTRY_CODES = (Object.keys(COUNTRIES) as Country[]).filter(
  code => code !== 'US'
);

const CA_PR_COUNTRY_CODES: Country[] = ['CA', 'PR'];

export const getDeviceShippingSchema = (
  t: TFunction,
  isInternationalFlow: boolean
) =>
  yup.object().shape({
    // A warehouse shipment never sets `receiverSite` (there's no site to
    // pick — the backend resolves the warehouse on its own), so it's not
    // required in that case.
    receiverSite: yup.lazy((_value, options) =>
      options?.parent?.deviceRetrieveAction === DeviceRetrieveAction.Warehouse
        ? yup.mixed().notRequired()
        : yup
            .object()
            .shape({
              name: yup.string().required(),
              value: yup.string().required(),
            })
            .required(t('offboardings.deviceRetrievalSchema.siteRequired'))
    ),
    // Shipping to one of Electric's global warehouses can be done in almost any country, but
    // shipping to a company site is restricted to CA/PR (the only countries
    // a site can be eligible in for this flow). Read the sibling field via
    // `options.parent` rather than a hook dependency, since this schema is
    // built once per form and deviceRetrieveAction can change afterwards.
    senderAddress: yup.lazy((_value, options) => {
      const deviceRetrieveAction = options?.parent?.deviceRetrieveAction;
      const allowedCountries = !isInternationalFlow
        ? (['US'] as Country[])
        : deviceRetrieveAction === DeviceRetrieveAction.Ship
          ? CA_PR_COUNTRY_CODES
          : NON_US_COUNTRY_CODES;
      return getRequiredAddressValidationSchema(allowedCountries);
    }),
    personalEmail: yup
      .string()
      .matches(
        emailRegex,
        t('offboardings.deviceRetrievalSchema.invalidEmail')
      ),
    senderPhone: getPhoneSchema(t),
    // A warehouse shipment (domestic or international) has no receiver phone
    // to enter: the backend resolves the warehouse's own phone rather than
    // receiving it from the frontend.
    receiverPhone: yup.lazy((_value, options) =>
      options?.parent?.deviceRetrieveAction === DeviceRetrieveAction.Warehouse
        ? yup.mixed().notRequired()
        : getPhoneSchema(t)
    ),
  });

// Every phone field uses InternationalPhoneInputField (whatever the country),
// which stores its value as an { phoneNumber, country } object rather than a
// plain string, so validate with an object schema instead of the shared
// string-based phoneValidationSchema helpers.
const getPhoneSchema = (t: TFunction) =>
  yup
    .object({
      phoneNumber: yup.string(),
      country: yup.string(),
    })
    .required(t('offboardings.deviceRetrievalSchema.phoneRequired'))
    .test(
      'is-valid-international-phone',
      t('offboardings.deviceRetrievalSchema.invalidPhone'),
      value => {
        if (!value?.phoneNumber) return false;
        try {
          return isPossiblePhoneNumber(
            value.phoneNumber,
            value.country as CountryCode
          );
        } catch {
          return false;
        }
      }
    );
